import React, { useState, useEffect, useContext } from "react";
import { useTheme } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import { getAllEmails, deleteEmail } from "../../api/Email/emailApi";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Paper,
  TextField,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";

import { SidebarContext } from "../Sidebar/SidebarProvider";

const EmailAdminPanel = () => {
  const theme = useTheme();
  const { isMinimized } = useContext(SidebarContext);
  const marginLeft = isMinimized ? "5em" : "16em";
  const marginRight = isMinimized ? "1em" : "1em";
  const [emails, setEmails] = useState([]);
  const [groupedEmails, setGroupedEmails] = useState({});
  const [selectedSender, setSelectedSender] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  /*   const [drawerOpen, setDrawerOpen] = useState(false); */
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState(null);
  const [senderFilter, setSenderFilter] = useState("");
  const [recipientFilter, setRecipientFilter] = useState("");

  // Extraer el userId del token almacenado en localStorage
  const token = localStorage.getItem("token");
  const decodedToken = token ? jwtDecode(token) : {};
  const userId = decodedToken.userId;

  // Evitar que los usuarios con los IDs 10 y 11 eliminen
  const isDeletionAllowed = ![10, 11].includes(userId);

  const styles = {
    container: {
      display: "flex",
      height: "80vh",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "8px",
      overflow: "hidden",
      backgroundColor: theme.palette.background.paper,
    },
    senders: {
      width: "25%",
      borderRight: `1px solid ${theme.palette.divider}`,
      overflowY: "auto",
      padding: "1em",
    },
    recipients: {
      width: "25%",
      borderRight: `1px solid ${theme.palette.divider}`,
      overflowY: "auto",
      padding: "1em",
    },
    emails: {
      width: "50%",
      padding: "1em",
      display: "flex",
      flexDirection: "column",
      overflowY: "auto",
    },
    messageBubble: {
      maxWidth: "90%",
      padding: "0px 20px",
      borderRadius: "10px",
      margin: "10px",
    },
    received: {
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.background.paper
          : "#f0f0f0",

      alignSelf: "flex-start",
    },
    sent: {
      /* backgroundColor: "#dcf8c6", */
      backgroundColor: theme.palette.background.paper,
      alignSelf: "flex-end",
      border: "2px solid #dcf8c6",
    },
    header: {
      padding: "10px",
      background: "#eee",
      borderBottom: "1px solid #ddd",
    },
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  useEffect(() => {
    groupEmailsBySender();
  }, [emails]);

  const fetchEmails = async () => {
    const data = await getAllEmails();
    setEmails(data);
  };

  const groupEmailsBySender = () => {
    const grouped = emails.reduce((acc, email) => {
      if (!acc[email.sender]) {
        acc[email.sender] = [];
      }
      acc[email.sender].push(email);
      return acc;
    }, {});
    setGroupedEmails(grouped);
  };

  const handleSelectSender = (sender) => {
    setSelectedSender(sender);
    setSelectedRecipient(null);
  };

  const handleSelectRecipient = (recipient) => {
    setSelectedRecipient(recipient);
  };

  const handleOpenConfirmDialog = (email) => {
    if (!isDeletionAllowed) {
      alert("You are not allowed to delete emails.");
      return;
    }

    setEmailToDelete(email);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setEmailToDelete(null);
  };

  const confirmDeleteEmail = async () => {
    if (emailToDelete) {
      try {
        await deleteEmail(emailToDelete.id);
        fetchEmails(); // Refresh the email list after deletion
      } catch (error) {
        console.error("Error deleting email:", error);
      } finally {
        handleCloseConfirmDialog();
      }
    }
  };

  // Aplicar filtros a los remitentes y destinatarios
  const filteredSenders = Object.keys(groupedEmails).filter((sender) =>
    sender.toLowerCase().includes(senderFilter.toLowerCase())
  );

  const filteredRecipients =
    selectedSender && groupedEmails[selectedSender]
      ? [
          ...new Set(
            groupedEmails[selectedSender]
              .map((email) => email.recipient)
              .filter((recipient) =>
                recipient.toLowerCase().includes(recipientFilter.toLowerCase())
              )
          ),
        ]
      : [];

  return (
    <Box
      sx={{
        marginLeft,
        marginRight,
        marginTop: "1em",
        backgroundColor: theme.palette.background.paper,
        padding: "0.5em 1em 1em 1em",
        borderRadius: "8px",
      }}
    >
      <h2>Email Panel</h2>

      <Box sx={styles.container}>
        {/* Sender List */}
        <Box sx={styles.senders}>
          <TextField
            label="Filter by Sender"
            variant="outlined"
            value={senderFilter}
            onChange={(e) => setSenderFilter(e.target.value)}
            fullWidth
            sx={{ marginBottom: 2 }}
          />
          <List>
            {filteredSenders.map((sender) => (
              <ListItem
                key={sender}
                button
                onClick={() => handleSelectSender(sender)}
                selected={selectedSender === sender}
              >
                <Avatar>{sender.charAt(0)}</Avatar>
                <ListItemText primary={sender} sx={{ marginLeft: "1em" }} />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Recipient List */}
        <Box sx={styles.recipients}>
          {selectedSender ? (
            <>
              <TextField
                label="Filter by Recipient"
                variant="outlined"
                value={recipientFilter}
                onChange={(e) => setRecipientFilter(e.target.value)}
                fullWidth
                sx={{ marginBottom: 2 }}
              />
              <List>
                {filteredRecipients.map((recipient) => (
                  <ListItem
                    key={recipient}
                    button
                    onClick={() => handleSelectRecipient(recipient)}
                    selected={selectedRecipient === recipient}
                  >
                    <ListItemText primary={recipient} />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Typography>Select a sender to view recipients</Typography>
          )}
        </Box>

        {/* Emails List */}
        <Box sx={styles.emails}>
          {selectedRecipient ? (
            groupedEmails[selectedSender]
              .filter((email) => email.recipient === selectedRecipient)
              .map((email) => (
                <Paper
                  key={email.id}
                  sx={{
                    ...styles.messageBubble,
                    ...(email.sender === selectedSender
                      ? styles.sent
                      : styles.received),
                    "&.MuiPaper-root": {
                      alignSelf: "center !important",
                      /*  maxWidth: "90%", */
                    },
                  }}
                >
                  <IconButton
                    onClick={() => handleOpenConfirmDialog(email)}
                    aria-label="delete"
                    sx={{
                      border: "2.5px solid red",
                      height: "35px",
                      width: "35px",
                      marginTop: ".5em",
                      alignSelf: "flex-end",
                    }}
                    disabled={!isDeletionAllowed}
                  >
                    <DeleteOutlineOutlinedIcon sx={{ color: "#000" }} />
                  </IconButton>
                  <div dangerouslySetInnerHTML={{ __html: email.body }} />
                </Paper>
              ))
          ) : (
            <Typography>Select a recipient to view emails</Typography>
          )}
        </Box>
      </Box>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">Confirm deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Are you sure you want to delete this email?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmDeleteEmail} color="primary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailAdminPanel;
